/**
 * Phase 23-B -- Cmd+K Global Command Palette.
 *
 * Deterministic, RBAC-aware, personalized-ranking palette powered by
 * `cmdk` under the hood but built on our shadcn `<Command>` primitives.
 *
 * Mounted once by `AppShell`, listens globally for Cmd+K / Ctrl+K to
 * toggle. Every selection navigates immediately AND fires an async
 * `/track` request so future rankings adapt to the user's habits.
 *
 * Server is the source of truth for RBAC + ranking; this UI adds:
 *   - 150 ms debounce on the query -> `/api/search/palette`
 *   - Grouped rendering (Quick Actions / Navigation / Recent)
 *   - Keyboard-first UX inherited from cmdk (arrows + Enter + Esc)
 *
 * NB: cmdk already de-dupes + filters client-side by label, so we set
 * `shouldFilter={false}` to trust the server ranking entirely.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuth } from "@/store/auth";
import api from "@/lib/api";
import {
  Home, Users, ShoppingCart, Building2, Wrench, FileText, Truck,
  BarChart3, Shield, ClipboardList, Calendar, Wallet, MapPin, Warehouse,
  FolderKanban, MessageCircle, CheckSquare, Package, DoorOpen, Eye,
  StickyNote, CalendarClock, CalendarDays, ConciergeBell, Search,
} from "lucide-react";

// Icon key (from the backend) -> lucide component. Keep in sync with
// `_STATIC_COMMANDS.icon` in `search_palette.py`.
const ICON_MAP = {
  "home": Home, "users": Users, "shopping-cart": ShoppingCart,
  "building": Building2, "wrench": Wrench, "file-text": FileText,
  "truck": Truck, "bar-chart": BarChart3, "shield": Shield,
  "clipboard-list": ClipboardList, "calendar": Calendar, "wallet": Wallet,
  "map-pin": MapPin, "warehouse": Warehouse, "folder-kanban": FolderKanban,
  "message-circle": MessageCircle, "square-check": CheckSquare,
  "package": Package, "door-open": DoorOpen, "eye": Eye,
  "sticky-note": StickyNote, "calendar-clock": CalendarClock,
  "calendar-days": CalendarDays, "concierge-bell": ConciergeBell,
  "id-card": Users,
};

const GROUP_LABEL = {
  quick: "Quick Actions",
  nav: "Navigation",
  recent: "Recent",
};

function useDebouncedValue(v, ms = 150) {
  const [debounced, setDebounced] = useState(v);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(v), ms);
    return () => clearTimeout(h);
  }, [v, ms]);
  return debounced;
}

export function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const debouncedQ = useDebouncedValue(query, 150);
  const activeAbort = useRef(null);

  // Global Cmd+K / Ctrl+K listener. Only mounted while authenticated.
  // Note: exclude Shift so Cmd+Shift+K stays exclusive to Quick Capture.
  useEffect(() => {
    const handler = (e) => {
      const isPaletteKey = (e.key === "k" || e.key === "K")
        && (e.metaKey || e.ctrlKey) && !e.shiftKey;
      if (isPaletteKey) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    // Programmatic trigger (topbar button, etc.).
    const openHandler = () => setOpen(true);
    window.addEventListener("open-command-palette", openHandler);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("open-command-palette", openHandler);
    };
  }, []);

  // Reset query each time the palette re-opens so stale text doesn't linger.
  useEffect(() => {
    if (!open) return;
    setQuery("");
  }, [open]);

  // Fetch results whenever the palette is open + debounced query changes.
  useEffect(() => {
    if (!open || !user) return;
    // Abort in-flight requests before firing a new one.
    if (activeAbort.current) activeAbort.current.abort();
    const ctrl = new AbortController();
    activeAbort.current = ctrl;
    setLoading(true);
    api.get("/search/palette", {
      params: { q: debouncedQ, limit: 30 },
      signal: ctrl.signal,
    }).then((res) => {
      setItems(res.data?.items || []);
    }).catch((e) => {
      // Silent -- palette is a convenience; failures shouldn't toast.
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        console.warn("palette fetch failed:", e?.message);
      }
    }).finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [open, debouncedQ, user]);

  const grouped = useMemo(() => {
    const g = { quick: [], nav: [], recent: [] };
    for (const it of items) g[it.group]?.push(it);
    return g;
  }, [items]);

  const onSelect = useCallback((it) => {
    setOpen(false);
    // Fire-and-forget tracking; navigation is what the user wants first.
    api.post("/search/palette/track", { command_key: it.command_key })
      .catch(() => { /* silent */ });
    if (it?.path) navigate(it.path);
  }, [navigate]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="overflow-hidden p-0 max-w-2xl gap-0"
        data-testid="cmdk-palette-dialog"
      >
        {/* Radix a11y -- a hidden title/description keeps screen readers happy
            without adding visible chrome to the palette. */}
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search commands, notes, tasks, meetings and people. Use arrow keys and
          Enter to select.
        </DialogDescription>
        <CommandPrimitive
          shouldFilter={false}
          className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder="Search commands, notes, tasks, people... (Ctrl+K)"
            value={query}
            onValueChange={setQuery}
            data-testid="cmdk-palette-input"
          />
          <CommandList data-testid="cmdk-palette-list">
            {!loading && items.length === 0 && (
              <CommandEmpty data-testid="cmdk-palette-empty">
                No matches. Try a different keyword.
              </CommandEmpty>
            )}

            {["quick", "nav", "recent"].map((groupKey) => {
              const rows = grouped[groupKey] || [];
              if (rows.length === 0) return null;
              return (
                <React.Fragment key={groupKey}>
                  {groupKey !== "quick" && <CommandSeparator />}
                  <CommandGroup
                    heading={GROUP_LABEL[groupKey]}
                    data-testid={`cmdk-group-${groupKey}`}
                  >
                    {rows.map((it) => {
                      const Icon = ICON_MAP[it.icon] || Search;
                      return (
                        <CommandItem
                          key={it.command_key}
                          value={`${it.command_key}::${it.label}`}
                          onSelect={() => onSelect(it)}
                          data-testid={`cmdk-item-${it.command_key}`}
                        >
                          <Icon className="h-4 w-4 opacity-70" strokeWidth={1.75} />
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm truncate">{it.label}</span>
                            {it.hint && (
                              <span className="text-[11px] text-muted-foreground truncate">
                                {it.hint}
                              </span>
                            )}
                          </div>
                          {typeof it.count === "number" && it.count >= 1 && (
                            <CommandShortcut>
                              {it.count >= 10 ? "10+" : Math.round(it.count)}x
                            </CommandShortcut>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </React.Fragment>
              );
            })}
          </CommandList>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

export default GlobalCommandPalette;
